<template>
  <div class="match-details">
    <nav class="navbar navbar-expand-lg navbar-light bg-light border-bottom shadow-sm mb-3">
      <div class="container">
        <router-link class="navbar-brand" to="/">
          <svg width="1.5em" height="1.5em" viewBox="0 0 16 16" class="back-icon bi bi-arrow-left-short" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <path fill-rule="evenodd" d="M12 8a.5.5 0 0 1-.5.5H5.707l2.147 2.146a.5.5 0 0 1-.708.708l-3-3a.5.5 0 0 1 0-.708l3-3a.5.5 0 1 1 .708.708L5.707 7.5H11.5a.5.5 0 0 1 .5.5z"/>
          </svg>
          <span>{{$t('general.matchdetails')}}</span>
        </router-link>
        <button class="btn btn-success" v-on:click="goToMatch()" v-if="!match.hasStarted">
          {{$t('details.startmatch')}}
        </button>
        <button class="btn btn-warning" v-on:click="goToMatch()" v-if="match.hasStarted">
          {{$t('details.continuematch')}}
        </button>
      </div>
    </nav>
    <transition name="fade">
      <div class="container space-below" v-if="matchLoaded">    
        <div class="form-group mb-3">  
          <div class="d-flex justify-content-between">
            <label class="form-label pt-2">{{$t('details.title')}}</label>  
            <button class="btn btn-link text-danger" v-on:click="deleteMatch()">{{$t('details.delete')}}</button>          
          </div>
          <input
            autocomplete="off"
            type="text"
            class="form-control" 
            id="opponent"
            ref="opponentInput"
            v-bind:placeholder="$t('details.titleplaceholder')"
            v-on:blur="saveMatch()"
            v-model="match.opponent"          
            @keyup.enter="focusAddPlayer"
          />
        </div>      
        <hr />      

        <div
          v-if="(!match.players || match.players.length == 0) && previousMatch && previousMatch.players && previousMatch.players.length > 0"
          class="mt-3 pt-2 text-muted"
        >
          <b>{{$t('details.reusePlayers')}}</b>
          <p class="mt-2">{{previousMatchPlayers}}</p>        
          <p class="mt-4"><button class="btn btn-primary" v-on:click="getPreviousMatchPlayers()">{{$t('details.reusePlayersButton')}}</button></p>  
        </div>

        <div
          v-if="(!match.players || match.players.length == 0) && previousMatch && previousMatch.players && previousMatch.players.length > 0"
          class="alert alert-warning mt-4"
        >
          {{$t('details.addPlayers')}}
        </div>

        <div
          v-if="(!match.players || match.players.length == 0) && (!previousMatch || !previousMatch.players || previousMatch.players.length === 0)"
          class="alert alert-warning"
        >
          {{$t('details.noPlayersAdded')}}          
        </div>

        
        <div v-for="player in match.players" :key="player.id">
          <div class="input-group mb-2" v-if="player._editing">
            <input
              autocomplete="off"
              type="text"
              class="form-control mr-2"
              id="playerName"
              v-bind:placeholder="$t('details.playerName')"
              v-model="player._editingName"                 
              @keyup.enter="savePlayer(player)"
            />
            <div class="input-group-append">
              <button class="btn btn-secondary" v-on:click="savePlayer(player)">
                {{$t('general.save')}}  
              </button>
            </div>
          </div>
          <div v-if="!player._editing" class="alert alert-primary py-2 mb-2">
            <div class="row">
              <div class="col">
                {{ player.name }}
              </div>
              <div class="col text-right">
                <a v-on:click="editPlayer(player)" class="text-dark">
                  <svg
                    width="1.2em"
                    height="1.2em"
                    viewBox="0 0 16 16"
                    class="bi bi-pencil"
                    fill="currentColor"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168l10-10zM11.207 2.5L13.5 4.793 14.793 3.5 12.5 1.207 11.207 2.5zm1.586 3L10.5 3.207 4 9.707V10h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.293l6.5-6.5zm-9.761 5.175l-.106.106-1.528 3.821 3.821-1.528.106-.106A.5.5 0 0 1 5 12.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.468-.325z"
                    />
                  </svg>
                </a>
                <a class="ml-2 text-dark" v-on:click="deletePlayer(player)">
                  <svg
                    width="1.5em"
                    height="1.5em"
                    viewBox="0 0 16 16"
                    class="bi bi-x"
                    fill="currentColor"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z"
                    />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
        
      </div>
    </transition>
    <transition name="fade">
      <nav class="navbar fixed-bottom navbar-light bg-light border-top" v-if="matchLoaded">
        <div class="container">
          <div class="input-group mb-1">
            <input
              autocomplete="off"
              type="text"
              class="form-control mr-2"
              id="newPlayerInput"
              ref="newPlayerInput"
              v-bind:placeholder="$t('details.playerName')"
              @keyup.enter="addPlayer"
              v-model="newPlayerName"
            />
            <div class="input-group-append">
              <button class="btn btn-secondary" v-on:click="addPlayer()">
                {{$t('details.add')}}
              </button>
            </div>
          </div>
        </div>
      </nav>
    </transition>
  </div>
</template>

<script lang="ts">
import { Vue } from "vue-class-component";
import moment from "moment";
import { set, get, del, keys } from "idb-keyval";
import { Match, Player } from "../models/Models";
import { Guid } from "guid-typescript";
import router from '../router';


export default class MatchDetails extends Vue {
  public match?: Match = new Match();
  public newPlayerName = "";
  public matchLoaded = false;
  
  public previousMatch: Match = new Match();
  public previousMatchPlayers: string;

  $refs: {
    newPlayerInput: HTMLFormElement;
    opponentInput: HTMLFormElement;
  };

  async created() {
    const id = (this as any).$route.params.id;
    this.match = await get(id);

    if (!this.match) {
      return;
    }

    this.sortPlayers();
    
    // get all matches to see if we can import players from the previous match
    const allMatchKeys = await keys();
    if (allMatchKeys.length > 1) {
      // if we only have one match, that is the current match - no need to try to find
      // the previous match
      let allMatches: Array<Match> = [];

      for (const key of allMatchKeys) {
        allMatches.push(await get(key));
      }

      allMatches = allMatches.sort((a, b) => b.created.localeCompare(a.created));

      for(const otherMatch of allMatches) {
        if (otherMatch.created < this.match.created) {
          this.previousMatch = otherMatch;

          let previousMatchPlayers = '';
          for(let i = 0; i < otherMatch.players.length; i++) {
            previousMatchPlayers += otherMatch.players[i].name;

            if (i + 2 < otherMatch.players.length) {
              previousMatchPlayers += ', ';
            } else if (i + 2 === otherMatch.players.length) {
              previousMatchPlayers += ' og ';              
            }
          }

          this.previousMatchPlayers = previousMatchPlayers;
          
          break;
        }
      }
    }

    if (!this.match) {
      return;
    }

    this.matchLoaded = true;

    if (!this.match.opponent || this.match.opponent == '') {      
      this.$refs.opponentInput.focus();
    } else {
      if (this.match.players.length == 0) {
        this.$refs.newPlayerInput.focus();
      }
    }

    if (this.match?.players.length == 0) {
      this.addPlayer();
    }
  }

  async getPreviousMatchPlayers() {
    if (!this.previousMatch) {      
      return;
    }

    this.previousMatch.players.forEach(p => {    
      const newPlayer: Player = {
        id: p.id,
        key: p.id,
        name: p.name,
        lastSecondsOnField: 0,
        lastSecondsOfField: 0,
        totalSecondsOnField: 0,
        lastOnField: undefined,
        lastOfField: undefined,
        isOnField: false,
        lastStartOnField: undefined,
        _editing: false,
        _editingName: undefined,
        _timeOnField: "00:00",
        _timeOffField: "00:00",
        _timeTotal: "00:00"
      };

      this.match?.players.push(newPlayer);
    });
    
    await this.saveMatch();
  }

  private editPlayer(player: Player) {
    player._editing = true;
    player._editingName = player.name;
  }

  private savePlayer(player: Player) {
    if (player._editingName) {
      player.name = player._editingName;
    }
    player._editing = false;

    this.saveMatch();
  }

  private deletePlayer(player: Player) {
    if (!this.match) {
      return;
    }

    this.match.players = this.match.players.filter(x => x.id != player.id);
    this.saveMatch();
  }

  public async deleteMatch() {
    if (confirm(this.$t('details.confirmDelete'))) {
      if (!this.match) {
        return;
      }
      await del(this.match.id);

      router.push({ name: "Matches"});
    }
  }

  private async addPlayer() {
    if (this.newPlayerName == "") {
      return;
    }

    if (this.match?.players) {
      const idx = Guid.create().toString();

      const newPlayer: Player = {
        id: idx,        
        key: idx,
        name: this.newPlayerName,
        lastSecondsOnField: 0,
        lastSecondsOfField: 0,
        totalSecondsOnField: 0,
        lastOnField: undefined,
        lastOfField: undefined,
        isOnField: false,
        lastStartOnField: undefined,
        _editing: false,
        _editingName: undefined,
        _timeOnField: "00:00",
        _timeOffField: "00:00",
        _timeTotal: "00:00"
      };

      this.match?.players.push(newPlayer);

      await this.saveMatch();

      this.newPlayerName = "";
      this.$refs.newPlayerInput.focus();
    }
  }

  async saveMatch() {
    if (!this.match) {
      return;
    }

    this.sortPlayers();

    const match = JSON.parse(JSON.stringify(this.match));
    await set(match.id, match);
  }

  goToMatch() {
    if (this.match) {
      router.push({ name: "PlayMatch", params: { id: this.match.id } });
    }
  }

  private sortPlayers() {
    if (!this.match) {
      return;
    }

    this.match.players = this.match.players.sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }

  focusAddPlayer() {
    this.$refs.newPlayerInput.focus();
  }
}
</script>
